export const APP_VERSION = "1.83.0";

// USER-FACING release notes shown in the in-app "What's new" dialog.
// One short, benefit-first sentence per bullet, written in English: say WHAT
// changed for the user, never HOW it works internally (no endpoints, cache,
// recompute, timeouts, column mechanics). Fixes start with "Fixed:" (renders
// a wrench). Full technical detail belongs in CHANGELOG.md, not here.
export const APP_CHANGELOG: { version: string; date: string; changes: string[] }[] = [
  {
    version: "1.83.0",
    date: "2026-09-30",
    changes: [
      "Explore TRACE feature by feature with interactive guidance on the real app pages, from rider extraction to the production day plan.",
      "Try the tools with a complete fictional festival: your demo changes stay separate from your own work.",
      "Pause, resume or skip the tour, with progress remembered for your account.",
      "Admin: clickable cards, rows and actions are easier to spot before you hover.",
      "Admin: the internal Design System is now available only to superadmins.",
    ],
  },
  {
    version: "1.82.0",
    date: "2026-09-08",
    changes: ["A helpful follow-up as you get started with TRACE."],
  },
  {
    version: "1.81.1",
    date: "2026-09-08",
    changes: [
      "Fixed: improved rider extraction reliability and access to plan features.",
    ],
  },
  {
    version: "1.81.0",
    date: "2026-09-03",
    changes: [
      "Admin: the dashboard now opens on what happened this week, who signed up and what they got done, with the platform totals underneath and a switch to leave internal accounts out.",
      "Admin: a new Festivals directory lists every festival on the platform, searchable, with its owner, organization, dates and counts.",
    ],
  },
  {
    version: "1.80.0",
    date: "2026-09-03",
    changes: [
      "You can now turn product-update emails on or off from Settings → Notifications.",
    ],
  },
  {
    version: "1.79.0",
    date: "2026-09-02",
    changes: [
      "Product updates now arrive by email, and every one of them carries an unsubscribe link at the bottom.",
    ],
  },
  {
    version: "1.78.0",
    date: "2026-09-02",
    changes: [
      "Need a hand? The new Help button in the sidebar sends your question, bug report or idea straight to us, and we reply by email.",
      "When your plan changes, you now receive an email that lists exactly what is included.",
    ],
  },
  {
    version: "1.77.0",
    date: "2026-09-02",
    changes: [
      "The Free plan now includes 10 AI rider extractions a month: upload your PDFs, review the extracted data and see the equipment totals of your festival. CSV export and the Builder and Scale intelligence stay in the paid plans.",
      "Fixed: the public home page no longer re-renders after loading, so it settles faster on phones.",
    ],
  },
  {
    version: "1.76.0",
    date: "2026-07-24",
    changes: [
      "Printable timetable: you can now export a single day and switch on vivid colors (solid stage color) for sheets that stay readable on real printers.",
      "Fixed: artist names are no longer cut off in print — the text adapts on its own (more lines, condensed font) and the page always comes out in landscape.",
    ],
  },
  {
    version: "1.75.7",
    date: "2026-07-24",
    changes: [
      "Export center reorganized: every row now shares the same structure — title and description at full width, options on a dedicated line and download buttons always in the same spot.",
    ],
  },
  {
    version: "1.75.6",
    date: "2026-07-22",
    changes: [
      "Console PDF: the artist list is reformatted for at-a-glance reading — larger, uniform text, an aligned SETUP / DRINKS / FOOD grid, a grey band for each artist, and an act never breaks across two pages.",
    ],
  },
  {
    version: "1.75.5",
    date: "2026-07-22",
    changes: [
      "Console PDF: under each DJ you now see ONLY the catering — Drinks, Food, Allergies, Diet, as in the Bar order list (Carico Bar). Transport, dressing room, guests and notes stay on the dedicated hospitality sheets.",
    ],
  },
  {
    version: "1.75.4",
    date: "2026-07-22",
    changes: [
      "Fixed: on unstable networks (the festival site) requests no longer die all together with \"server unreachable\" — reads retry on their own over a fresh connection and the maximum wait time is more realistic.",
    ],
  },
  {
    version: "1.75.3",
    date: "2026-07-22",
    changes: [
      "Console PDF: the list under the layout now shows each DJ's ASSIGNED SETUP (identical to the Console designer cards), no longer the raw rider requests.",
      "Console PDF: schematic hospitality in bar-list style — DRINKS, FOOD, ALLERGIES (in red) and the rest in compact rows with just the items, without losing anything.",
    ],
  },
  {
    version: "1.75.2",
    date: "2026-07-21",
    changes: [
      "Console PDF: each DJ's hospitality is COMPLETE again under the layout — the stage manager reads everything there, drinks included; long lists continue on the next page.",
    ],
  },
  {
    version: "1.75.1",
    date: "2026-07-21",
    changes: [
      "Fixed: in the console PDF the DJs are now in true timetable order (sets after midnight at the end of the night) and the hospitality is compact — at most two lines per DJ, with a pointer to the hospitality sheet for the detail.",
    ],
  },
  {
    version: "1.75.0",
    date: "2026-07-21",
    changes: [
      "Console layout PDF: under each by-day layout you can print the list of DJs in running order with their technical requests and hospitality — the stage manager reads everything from one sheet. Switch it on with the \"DJ requests + hospitality\" checkbox in the Export center.",
    ],
  },
  {
    version: "1.74.1",
    date: "2026-07-21",
    changes: [
      "Crew: a Delete shift button right in the edit dialog — the first tap arms it, the second confirms, no system popup.",
      "Notifications: on first access a card offers to turn on push notifications (never if you already declined); devices already authorized re-register on their own.",
      "Notification sound: a distinctive chime when a notification arrives while the app is open — you can turn it off in settings.",
    ],
  },
  {
    version: "1.74.0",
    date: "2026-07-21",
    changes: [
      "Crew: drag a shift onto another row to change its venue (or its production, in the Production view) — no need to open the dialog anymore.",
      "Crew: configurable print — choose A4/A3, landscape or portrait and the margins; your preferences stay saved.",
      "Crew: safe edit covers shifts too — with the toggle on, every move asks for confirmation before saving.",
      "Crew: the handles to stretch or shorten a shift from its edges are wider and more visible, on tablets too.",
    ],
  },
  {
    version: "1.73.3",
    date: "2026-07-21",
    changes: [
      "Venues are created from the Crew tab — a new button next to the day (and in the menu on phones), where you actually use them.",
      "New \"All venues\" and \"All stages\" filters in Crew: view and print only the locations or only the stages, alongside the existing filters.",
    ],
  },
  {
    version: "1.73.2",
    date: "2026-07-21",
    changes: [
      "Fixed: a stage with artists on the schedule can no longer be turned into a venue by mistake — the lineup cannot silently disappear from the views.",
      "Fixed: venues no longer show up in the console layout PDF, the Equipment board, the \"N stages\" counts or the technical crew numbers.",
      "Crew batch: new Production field — when you add a whole team, the shifts come out already tagged with its production.",
      "Fixed: section filters also work in the Production view and on the mobile grid (the team filter no longer shows an empty page).",
    ],
  },
  {
    version: "1.73.1",
    date: "2026-07-21",
    changes: [
      "Real venues: create a location (farmhouse, beach) from + → Venue — it lives only in Crew and Logistics, and never appears in the lineup, the DJ grid, the changeovers or the schedule exports.",
      "Fixed: a venue can no longer end up among the DJ stages by mistake — it is a place for the crew, not a stage.",
    ],
  },
  {
    version: "1.73.0",
    date: "2026-07-20",
    changes: [
      "Crew: every shift can belong to a production (e.g. Producer Masseria, Beach Production) — new \"Production\" view in the timeline, plus filter and print by production, independent of the shift's venue.",
      "Crew: pick the production in the shift dialog; for people on a single team it fills in on its own, for crossover people it stays a manual choice.",
      "Crew: venues (farmhouse, beach…) have their own selector, separate from stages — mark a row as \"Venue\" in Manage stages and it shows up in the Venue field when assigning.",
      "Assistant: it understands productions — \"put the beach production on Cala Maka\" creates the shifts already tagged with the right production, even for people who work across several teams.",
    ],
  },
  {
    version: "1.72.0",
    date: "2026-07-20",
    changes: [
      "Fixed: crew shifts no longer look moved to the next day — morning times stay on the day you chose, even for shifts that end at night.",
      "Crew: every venue/stage always appears in the timeline, even on days without shifts — you can assign people to a venue on any day.",
    ],
  },
  {
    version: "1.71.1",
    date: "2026-07-20",
    changes: [
      "Fixed: the assistant now handles large shift plans too (a week across several venues) — proposals arrive split by day, one card to confirm at a time, with no more timeouts.",
    ],
  },
  {
    version: "1.71.0",
    date: "2026-07-20",
    changes: [
      "Assistant: it now organizes crew shifts too — ask it to put a whole team on a stage or to cover the gaps of a day, and apply all the proposed shifts with a single confirmation.",
      "Crew: production people can now be assigned to a specific stage or venue (the stage field is available and optional for production roles too).",
      "Crew: stage headcount now counts technical roles only — people on a stage as venue staff (social, hospitality, production) stay visible but no longer hide gaps in technical coverage.",
    ],
  },
  {
    version: "1.70.0",
    date: "2026-07-20",
    changes: [
      "Crew: phone numbers are now visible on the shift bars in the timeline and in the printable PDF — on a dedicated line, and next to the bar when the shift is too short to hold it.",
      "Fixed: the printable timetable, the Timetable sheet of the production pack and the PDF report showed set times in UTC (two hours behind) — they are now always in the festival's time zone.",
    ],
  },
  {
    version: "1.69.0",
    date: "2026-07-20",
    changes: [
      "Production plan: new Gantt export as an A3 or A2 PDF, ready to print and pin up, with every activity and label fully readable.",
      "Production plan: the team column now widens and narrows by dragging its edge (double-click to return to the standard width).",
      "Assistant: it can now update the production plan for you — it adds, edits or deletes activities and applies the change only after you confirm on a card in the chat.",
    ],
  },
  {
    version: "1.68.12",
    date: "2026-07-20",
    changes: [
      "iPhone PWA: the chat stays full-screen with no white band, even after using the keyboard.",
    ],
  },
  {
    version: "1.68.11",
    date: "2026-07-20",
    changes: [
      "iPhone PWA: the chat locks the background without resizing the window, removing the bounce and preventing the band left behind on close.",
    ],
  },
  {
    version: "1.68.10",
    date: "2026-07-20",
    changes: [
      "iPhone PWA: closing the assistant now snaps the screen back automatically, so the chat no longer leaves a white band at the bottom.",
    ],
  },
  {
    version: "1.68.9",
    date: "2026-07-20",
    changes: [
      "iPhone PWA: removed the constraint that could shorten the window, while keeping zoom locked in the installed app — requires reinstalling from the Home screen.",
    ],
  },
  {
    version: "1.68.8",
    date: "2026-07-20",
    changes: [
      "PWA: the app draws full-screen up to the top edge again (native look, no system strip). Requires one last reinstall from the Home screen.",
    ],
  },
  {
    version: "1.68.7",
    date: "2026-07-20",
    changes: [
      "PWA: the status bar at the top now matches the app color (light and dark) — no more mismatched white strip, a native-app look.",
    ],
  },
  {
    version: "1.68.6",
    date: "2026-07-20",
    changes: [
      "PWA: the white strip at the bottom is gone for good — it was an iOS defect with the translucent status bar; the app now uses the standard mode. Requires reinstalling the app from the Home screen (delete the icon → Safari → Add to Home Screen).",
    ],
  },
  {
    version: "1.68.5",
    date: "2026-07-20",
    changes: [
      "Fixed: the white strip that appeared when opening the assistant in the PWA and stayed there — it was the iOS keyboard shifting the window without putting it back. The chat now opens without the keyboard (tap the box when you want to type) and after typing the window snaps back on its own.",
    ],
  },
  {
    version: "1.68.4",
    date: "2026-07-20",
    changes: [
      "PWA: the white band under the chat is gone (that area now has the app color) and the chat no longer drags sideways with your finger.",
    ],
  },
  {
    version: "1.68.3",
    date: "2026-07-20",
    changes: [
      "Screen diagnostics tool for admins (bug-shaped button at the bottom left): shows the display measurements in real time to pin down PWA issues.",
    ],
  },
  {
    version: "1.68.2",
    date: "2026-07-20",
    changes: [
      "PWA: the screen no longer drags with your finger and the empty band at the bottom no longer appears while the assistant is open (the background now stays locked).",
    ],
  },
  {
    version: "1.68.1",
    date: "2026-07-20",
    changes: [
      "PWA: firmer lock against accidental zoom/panning of the screen (iOS was ignoring the previous setting).",
    ],
  },
  {
    version: "1.68.0",
    date: "2026-07-19",
    changes: [
      "A steadier app on phones: no more accidental zoom/panning of the screen in the PWA, and the AI assistant chat fills the screen to the bottom with no empty spaces.",
    ],
  },
  {
    version: "1.67.0",
    date: "2026-07-19",
    changes: [
      "New AI assistant composer: a rounded field that grows as you type and shrinks back after sending — like a real chat.",
    ],
  },
  {
    version: "1.66.0",
    date: "2026-07-19",
    changes: [
      "Fixed: the AI assistant on phones no longer auto-zooms when the keyboard opens (which cut off the edges and left a white band) — the chat stays full-screen and stable.",
      "Notifications are fully visible on phones/PWA too: the panel no longer runs off the screen.",
      "Stage notifications now say who changed what (e.g. \"Giuseppe changed the color of Magma Stage\"), instead of a generic \"updated\".",
    ],
  },
  {
    version: "1.65.0",
    date: "2026-07-19",
    changes: [
      "Full-screen AI assistant on phones: it opens like a real chat, no longer as a side column, and it no longer overlaps the status bar at the top nor leaves empty bands at the bottom.",
      "PDP: in the Agenda view on phones the time column is narrower, so activity names get more room.",
    ],
  },
  {
    version: "1.64.1",
    date: "2026-07-19",
    changes: [
      "Fixed: confirming a large PDP import could show a timeout error even when the import had actually succeeded — it now waits as long as needed and recognizes an import that already completed.",
    ],
  },
  {
    version: "1.64.0",
    date: "2026-07-18",
    changes: [
      "Days added from the Crew tab now stay there: no more build days showing up in the festival calendar or the artist grid. And a crew day added by mistake can be removed with the new button next to the calendar.",
    ],
  },
  {
    version: "1.63.0",
    date: "2026-07-18",
    changes: [
      "The PDP import draft is no longer lost: close the panel freely — when you reopen it you find the analysis already done (no new AI call), and to change file there is \"Discard and upload another file\" with confirmation.",
    ],
  },
  {
    version: "1.62.0",
    date: "2026-07-18",
    changes: [
      "A more comfortable PDP import: the panel widens by dragging its edge (and remembers the width), during the analysis you see the rows being recognized, and if the file has empty sheets it tells you right away — so you spot an old export at a glance.",
    ],
  },
  {
    version: "1.61.1",
    date: "2026-07-18",
    changes: [
      "Fixed: during a PDP import some rows could arrive as raw drafts to fix by hand instead of already recognized — they are now read correctly in every case.",
    ],
  },
  {
    version: "1.61.0",
    date: "2026-07-18",
    changes: [
      "Reset the PDP in one go: in the production plan's \"···\" menu you find \"Reset the whole plan\" — it empties every day (and optionally teams and areas too) with a double confirmation, so you can re-import the Excel from scratch without deleting day by day.",
    ],
  },
  {
    version: "1.60.2",
    date: "2026-07-18",
    changes: [
      "Faster, more reliable PDP import: the AI analysis now works in parallel (from minutes to under a minute), no row of the file can be lost silently anymore, and the confidence percentages truly reflect how much there is to double-check.",
    ],
  },
  {
    version: "1.60.1",
    date: "2026-07-18",
    changes: [
      "PDP: the Gantt chart now fits the day's hours instead of always showing all 24 — blocks stay readable even when there are few activities.",
    ],
  },
  {
    version: "1.60.0",
    date: "2026-07-18",
    changes: [
      "Import a production plan from Excel: upload your PDP and TRACE recognizes days, times, teams and activities, shows them to you for review row by row, and adds them to the plan only after your confirmation.",
    ],
  },
  {
    version: "1.59.1",
    date: "2026-07-18",
    changes: [
      "Production is more usable on phones: the same day selector in every tab (Equipment, Console, PDP, Crew), the PDP filters in a single panel, and Crew shows a vertical list instead of the cramped timeline.",
    ],
  },
  {
    version: "1.59.0",
    date: "2026-07-17",
    changes: [
      "Crew: you can now add days even weeks before the festival (calendar button next to the days) — production shifts start when they need to.",
      "Crew: filter by section (Production, single stage, Support) on the timeline and grid, and the Print button exports only what you see.",
      "Fixed: saving the festival's extra dates was failing.",
    ],
  },
  {
    version: "1.58.0",
    date: "2026-07-17",
    changes: [
      "The PDP Gantt now zooms: three zoom levels + \"Fit\", horizontal scroll with team names always visible — even a half-hour slot reads well, and the view opens right on the first activity of the day.",
      "The Bar order list (Carico Bar) summary now has brand detail: spirits and wines bottle by bottle, per day, with the artists who ask for them.",
      "PDP Agenda redone: the checkbox marks Done with a single click, large well-spaced times, colors consistent with the Gantt and morning→evening ordering you can flip.",
    ],
  },
  {
    version: "1.57.1",
    date: "2026-07-17",
    changes: [
      "Fixed: the Bar order list (Carico Bar) recognizes more real products from the riders — plural beers, generic soft drinks, tequila brands, glasses and lemons land in the right categories.",
    ],
  },
  {
    version: "1.57.0",
    date: "2026-07-17",
    changes: [
      "The Bar order list (Carico Bar) is a true operational document: colored sections per day and stage, categories (Water, Soft drinks, Beer, Wine, Spirits…), yellow where the rider gives no quantity, red rows for allergies and diets, a summary by category and the artists without a rider.",
    ],
  },
  {
    version: "1.56.0",
    date: "2026-07-17",
    changes: [
      "Fixed: the AI assistant now finishes long answers — no more half-done reasoning you have to nudge along.",
      "More readable answers: real tables, a Copy button on blocks and messages, and the files you ask for (CSV or Excel) download straight from the chat.",
      "The assistant now also knows the production plan (PDP) and the bar list — ask it about calls, vehicles and bottles.",
      "The assistant window widens by dragging its left edge.",
    ],
  },
  {
    version: "1.55.1",
    date: "2026-07-17",
    changes: [
      "Fixed: in the PDP the time you type applies to the chosen day — 05:00 is the morning, no longer a surprise \"after midnight\"; the night is turned on only with the dedicated link.",
    ],
  },
  {
    version: "1.55.0",
    date: "2026-07-17",
    changes: [
      "The PDP Gantt now speaks the same language as Grid and Crew: a full 24-hour day, rows colored by team or area, time always visible on every block.",
      "Night times without thinking about it: type 02:00 and TRACE works out on its own that it is after midnight (one click to say \"it is a morning call\") — the \"+1\" switches are gone.",
      "Lighter PDP toolbar: day actions and exports gathered in two menus.",
      "The bar list now includes artists without hospitality too: red rows in the \"By artist\" sheet, so nobody disappears from the bar's list.",
    ],
  },
  {
    version: "1.54.1",
    date: "2026-07-16",
    changes: [
      "New \"Bar order list\" in the Export center: all the drinks from the riders aggregated into one Excel for the bar — identical bottles merged, quantities summed, with tabs for totals, day, stage and artist.",
      "Clearer PDP: the time switches now have labels (\"Time TBC\", \"after midnight +1\") and areas are created straight from the entry sheet — including custom ones that are not stages.",
    ],
  },
  {
    version: "1.54.0",
    date: "2026-07-15",
    changes: [
      "New PDP tab in Production: the production plan lives in TRACE — build/show/strike days, teams and suppliers, calls, vehicles, meals and milestones on a draggable timeline or in an hourly agenda.",
      "When something slips, \"Shift\" moves the rest of the day in one click; \"Copy day\" duplicates a whole day.",
      "The PDP comes out as always: a PDF of the day ready to print and sign, an Excel with one sheet per day identical to your format, and CSV.",
    ],
  },
  {
    version: "1.53.0",
    date: "2026-07-14",
    changes: [
      "Contradictions between riders can now be closed: one click on \"Mark as decided\" (with an optional note) and the red goes away — it reopens on its own if a new rider arrives that changes the picture.",
      "Promoter confirmations become a real checklist: tick the confirmed rows, filter the pending ones and see who confirmed what (in the CSV too).",
    ],
  },
  {
    version: "1.52.3",
    date: "2026-07-14",
    changes: [
      "Fixed: in the Console some acts showed times two hours behind the grid — all times now use the festival's time zone.",
    ],
  },
  {
    version: "1.52.2",
    date: "2026-07-14",
    changes: [
      "Cleaner act workspace: collapsible sections are now discreet links instead of large empty boxes.",
    ],
  },
  {
    version: "1.52.1",
    date: "2026-07-14",
    changes: [
      "Admin panel, organization management and artist roster are faster and lighter on the database.",
    ],
  },
  {
    version: "1.52.0",
    date: "2026-07-14",
    changes: [
      "More precise rider classification (live electronic acts no longer end up among the bands), and correctable by hand from the review page.",
      "Clearer act workspace: a guide to document types, a B2B hint and no more technical jargon.",
      "Lighting riders now reach the supplier: new Lighting sheet in the production pack.",
    ],
  },
  {
    version: "1.51.0",
    date: "2026-07-14",
    changes: [
      "The language chosen in the Export center now also applies to festival reports, timetables, crew sheets, transport Gantt and console layouts.",
      "Dedicated lighting riders and stage plots are recognized automatically from the PDF's name or cover page, always keeping the manual correction.",
      "Fixed: the landing page with reduced motion and hospitality extractions with empty sections no longer produce technical errors.",
    ],
  },
  {
    version: "1.50.0",
    date: "2026-07-13",
    changes: [
      "PDFs added to an act keep extracting and linking on the server even if you close the panel, refresh the page or come back later.",
      "You can upload Technical, Hospitality, Lighting and Stage plot sources together: each document is added to the act without replacing the ones already there.",
      "Fixed: lost responses, retries and document-type corrections now recover the job already saved without duplicating uploads or extraction work.",
    ],
  },
  {
    version: "1.49.4",
    date: "2026-07-13",
    changes: [
      "The Production Pack now includes a Logistics sheet with planned transfers, driver and vehicle assignments, hotels, rooms and flights, in Italian too.",
    ],
  },
  {
    version: "1.49.3",
    date: "2026-07-13",
    changes: [
      "Festivals with many stages and artists open faster and without timeouts: the detail loads only the schedule and the names it needs, not unrelated operational data.",
    ],
  },
  {
    version: "1.49.2",
    date: "2026-07-12",
    changes: [
      "Fixed: on desktop the festival pages no longer show a large white band under the sidebar and the content.",
    ],
  },
  {
    version: "1.49.1",
    date: "2026-07-12",
    changes: [
      "The mobile menu is now a wider, rounder glassy capsule, with five always-recognizable icons and an active state similar to native apps.",
      "Search is available right in the dock and opens the global search full-screen without losing the current festival.",
      "Fixed: toasts, the AI assistant and bottom panels all respect the same safe area and no longer overlap the new dock.",
    ],
  },
  {
    version: "1.49.0",
    date: "2026-07-11",
    changes: [
      "On mobile, Grid, Run of Show, Equipment, Console, Crew and Logistics show the operational content right away, with a much more compact header and controls.",
      "Stage names stay readable in the Grid; color and deletion are gathered in the context menu without widening the timeline.",
      "The mobile Console opens straight onto the canvas and keeps stage, day, status and save in the command deck, with tools available on demand.",
      "The new bottom dock keeps the current festival when you open Production and recognizes the Console correctly too.",
      "You can drag the AI assistant to whichever edge you prefer and its position is remembered, without permanently covering the controls.",
    ],
  },
  {
    version: "1.48.0",
    date: "2026-07-11",
    changes: [
      "The festival has a single, more compact interface: dates always in the same position, aligned actions and consistent controls across Schedule, Equipment, Console, Crew and Logistics.",
      "Every act has a new single space for Requirements and Documents, where you add several technical, hospitality, lighting and stage plot PDFs without replacing existing sources.",
      "You can add an artist straight from their stage and day, even without a rider yet.",
      "On mobile, header, menu, panels and buttons were rethought to avoid overflow and offer native-app-like Back navigation.",
      "Fixed: upload retries recover the rider already saved, and new acts after midnight are no longer placed on top of existing sets.",
    ],
  },
  {
    version: "1.47.3",
    date: "2026-07-11",
    changes: [
      "Mixed riders stay in processing until Technical and Hospitality are ready together, keeping the two sheets separate.",
      "During a new extraction the last approved data stays visible, with no temporary conflicts or equipment requests.",
      "Fixed: a tab left open for a long time can no longer overwrite rider data updated in the meantime.",
      "Fixed: on iPhone the Equipment detail has a reachable Back button and also closes with the gesture or the Back command.",
    ],
  },
  {
    version: "1.47.2",
    date: "2026-07-11",
    changes: [
      "Fixed: changes to sets, stages and soundchecks arrive via push on the other devices of the account making the edit too.",
      "On mobile the bottom menu sits closer to the edge and leaves more room for content without invading the Home gesture area.",
      "In mobile Settings each section now opens on its own under a stable selector, without overlapping the content while scrolling.",
    ],
  },
  {
    version: "1.47.1",
    date: "2026-07-11",
    changes: [
      "You can add an artist to the Stage Grid by hand without a rider yet and link the documents later.",
      "Fixed: hospitality-only riders are recognized correctly and add to the existing technical data without replacing it.",
    ],
  },
  {
    version: "1.47.0",
    date: "2026-07-11",
    changes: [
      "Every act can now have several technical, hospitality, lighting and stage plot riders, with priority and versions managed from Source documents.",
      "TRACE builds unified effective requirements per act, keeps the provenance and flags contradictions without blindly summing duplicate requests.",
      "New operational push notifications for crew and organization: set and soundcheck in 15 minutes, time changes and changes to CDJs or mixers.",
      "Fixed: in the mobile PWA you can now go back with a contextual control, and notifications open the right screen directly.",
    ],
  },
  {
    version: "1.46.1",
    date: "2026-07-11",
    changes: [
      "Fixed: Run of Show times now match the Stage Grid and always respect the festival's local time zone, even after midnight.",
      "Manual events such as soundcheck, transfer and hospitality can now be linked to an act and a stage of the festival, or to a custom artist or place.",
    ],
  },
  {
    version: "1.46.0",
    date: "2026-07-11",
    changes: [
      "A steadier dashboard during updates, light theme by default and a simplified Riders area with Library and Upload in the same space.",
      "New Signal Lock sign-in transition between login and dashboard.",
      "Fixed: mixer conflicts now respect manual assignments and saved Console Designer layouts, without re-proposing rider requests already settled.",
    ],
  },
  {
    version: "1.45.3",
    date: "2026-07-10",
    changes: [
      "New \"High visibility\" option in Settings → Appearance: more vivid stage and timeline colors and higher-contrast text, to read the grid in bright environments or in the sun during the festival.",
    ],
  },
  {
    version: "1.45.2",
    date: "2026-07-10",
    changes: [
      "A stage color is now saved when you settle on the chosen shade, not on every movement of the picker — smoother and with no slowdowns.",
    ],
  },
  {
    version: "1.45.1",
    date: "2026-07-10",
    changes: [
      "Fixed: editing stages and slots (colors, drags, reorders) could slow everything down or fail on large festivals — it is now immediate.",
    ],
  },
  {
    version: "1.45.0",
    date: "2026-07-09",
    changes: [
      "Admin: new API cost dashboard — redesigned usage chart, period selector with end-of-month projection and a per-user table detailing who spends what.",
    ],
  },
  {
    version: "1.44.1",
    date: "2026-07-09",
    changes: [
      "Fixed: on very heavy questions the assistant could still stall with the English message \"tool-call budget\" — it now always answers with what it gathered, in your language.",
    ],
  },
  {
    version: "1.44.0",
    date: "2026-07-08",
    changes: [
      "Console Designer: you now drag the fomets freely on the canvas — L-shaped booths, detached fomets, a movable Ready cases area and rotation of a single fomet.",
      "Console layouts already saved stay identical and move to the new format only on the next save.",
    ],
  },
  {
    version: "1.43.0",
    date: "2026-07-08",
    changes: [
      "The AI assistant now works out the hospitality totals of all artists in a single answer — bottles and drinks, catering, guests.",
      "Fixed: on very broad questions the assistant no longer stalls with an English message — it answers with what it found, in your language.",
    ],
  },
  {
    version: "1.42.2",
    date: "2026-07-08",
    changes: [
      "Fixed: re-downloading an export could return the old version from the browser cache — downloads are now always fresh.",
    ],
  },
  {
    version: "1.42.1",
    date: "2026-07-08",
    changes: [
      "A more readable hospitality PDF: no more blank first page, one day per page, festival totals at the end and page numbers.",
    ],
  },
  {
    version: "1.42.0",
    date: "2026-07-08",
    changes: [
      "In the festival pack the Schedule now has a Console column: for each set you see the console assigned to that DJ.",
      "The Crew tab of the pack now colors the stages with the same colors as the other sheets.",
    ],
  },
  {
    version: "1.41.1",
    date: "2026-07-08",
    changes: [
      "Fixed: the rider quick edit now starts from the model already assigned (not the one written in the rider) and changing it keeps the artist's original request.",
      "In the equipment exports \"Variants\" is now called \"Exact models\": it is the binding material to supply, not an alternative.",
    ],
  },
  {
    version: "1.41.0",
    date: "2026-07-08",
    changes: [
      "Click an act in the console panel to open its sheet and correct the rider's CDJs, mixer and turntables on the fly — without going through Equipment.",
    ],
  },
  {
    version: "1.40.0",
    date: "2026-07-08",
    changes: [
      "The Hospitality export now has targeted cuts: catering only (with festival totals for the bar order), travel and hotels, or guest lists.",
    ],
  },
  {
    version: "1.39.0",
    date: "2026-07-08",
    changes: [
      "The console panel now shows every act of the night: green for those with a rider, yellow for live acts with their own gear, red for those still without a rider.",
    ],
  },
  {
    version: "1.38.0",
    date: "2026-07-08",
    changes: [
      "New Hospitality export: a standard sheet per artist with catering, dressing room, guests and transport — by day, stage or artist.",
      "Fixed: the order of times in the equipment request lists — sets after midnight now come after the evening ones.",
      "The console's Peak view is now hidden by default: bring it back with the Peak view button if you need it.",
      "Fixed: adding an element to the console no longer shifts the rest of the booth.",
      "Fixed: unsaved console changes no longer vanish on their own after a save or a window switch.",
      "A notice now tells you when an added element lands in the Ready cases because there is no room on the booth.",
      "In the equipment detail, requests are grouped by stage — and clicking a stage's cell shows only that stage.",
      "Fixed: downloading riders or exports with accented names (è, à, ò) no longer fails.",
    ],
  },
  {
    version: "1.37.0",
    date: "2026-07-08",
    changes: [
      "The console drawing now shows real photos of the Rane MP2015, Pioneer RMX-1000 and laptop, with correct footprints.",
      "Fixed: the order of the per-day console PDF — pages are now grouped by night, not by stage.",
      "The Export window is more readable: better-separated sections and rows of equal height.",
    ],
  },
  {
    version: "1.36.1",
    date: "2026-07-07",
    changes: [
      "Fixed: an error that blocked the festival pack download when effects were on the list.",
    ],
  },
  {
    version: "1.36.0",
    date: "2026-07-07",
    changes: [
      "Effects requested by DJs (e.g. RMX-1000) now appear in the equipment list, with a choice among the rider's alternatives.",
      "The console layout PDF export is now day by day: each page shows the real booth for that night.",
    ],
  },
  {
    version: "1.35.4",
    date: "2026-07-07",
    changes: [
      "Fixed: a timeout that could appear when changing spares or stock on large festivals.",
    ],
  },
  {
    version: "1.35.3",
    date: "2026-07-07",
    changes: [
      "The Equipment table now signals when it is refreshing totals after an edit.",
    ],
  },
  {
    version: "1.35.2",
    date: "2026-07-07",
    changes: [
      "In an item's detail you find the \"To order\" summary: how many to actually order, spares included.",
    ],
  },
  {
    version: "1.35.1",
    date: "2026-07-07",
    changes: [
      "Editing spares and stock is now immediate, with no waiting.",
    ],
  },
  {
    version: "1.35.0",
    date: "2026-07-07",
    changes: [
      "You now decide spares stage by stage: in an item's detail choose Auto, none or a fixed number. The choice applies everywhere, from the grid to the exports.",
    ],
  },
  {
    version: "1.34.1",
    date: "2026-07-07",
    changes: [
      "Clearer equipment order: the column to order (spares included) is highlighted and every sheet of the pack explains how to read it.",
    ],
  },
  {
    version: "1.34.0",
    date: "2026-07-07",
    changes: [
      "Export center: all the festival's exports in a single panel, with clear names and Italian labels.",
      "A more complete Production Pack: it now includes the schedule and the crew too.",
    ],
  },
  {
    version: "1.33.2",
    date: "2026-07-06",
    changes: [
      "Console: when you change a model in the rider review, Console layouts already saved realign to the updated requests. Old request cards, such as a replaced mixer, no longer linger in the saved day.",
    ],
  },
  {
    version: "1.33.1",
    date: "2026-07-06",
    changes: [
      "Equipment export: all band/live gear requested from production now counts as a request for the supplier, even when the rider says venue, house or rental. The Excel will no longer show \"Supplied by: venue\" for that gear; only what the artist actually brings stays out of the supplier counts.",
    ],
  },
  {
    version: "1.33.0",
    date: "2026-07-04",
    changes: [
      "Stages: you now reorder and recolor directly from the Grid. Drag the handle next to a stage to reorder it (owner/manager), and tap the colored dot to pick its color — both hold across every view and every day.",
      "Stages: the color no longer changes when you move a stage. Each stage keeps its color even if you reorder it (before, the \"automatic\" colors reshuffled on every move). It applies everywhere: Grid, Timeline, Console, Changeover, Crew, Dashboard, Equipment.",
      "\"Manage stages\" stays available and shares the same behavior as the Grid, so order and colors are always consistent.",
    ],
  },
  {
    version: "1.32.1",
    date: "2026-07-04",
    changes: [
      "Fixed: in \"Manage stages\", dragging a stage could leave the order wrong and move other stages you were not touching. Reordering is now precise even with several quick drags in a row, and changing a stage's color no longer affects the order.",
    ],
  },
  {
    version: "1.32.0",
    date: "2026-07-04",
    changes: [
      "Stages: a fixed, customizable order. Stages no longer reorder themselves when you edit them (it was a bug). With \"Manage stages\" (owner/manager) you drag the stages into the order you want — the same for everyone, in Console, Grid and Timeline.",
      "Stages: a color of your choice. Also from \"Manage stages\" you assign a color to each stage (6 presets or a free color); it propagates to every view and every day, and no longer changes on its own.",
    ],
  },
  {
    version: "1.31.3",
    date: "2026-07-04",
    changes: [
      "AI assistant: the guide (the \"?\" in the panel) now suggests many more questions for each category — schedule, stages, setup, hospitality, soundcheck, travel, hotels, transport, changeovers, equipment, crew, conflicts, production status, contacts and summaries. Every question is verified: the assistant can really answer it.",
    ],
  },
  {
    version: "1.31.2",
    date: "2026-07-03",
    changes: [
      "AI assistant: new search across all riders at once. You can now ask \"who has allergies or intolerances?\", \"who asks for a DJM-V10?\", \"who wants mezcal in the dressing room?\" and the assistant combs through every rider of the festival and answers with the list of artists and the detail.",
    ],
  },
  {
    version: "1.31.2",
    date: "2026-07-03",
    changes: [
      "Console: Peak layouts are inherited by the single days until you save a dedicated layout for that day.",
      "Console: new Arrange to redistribute items with the mixer in the middle, CDJs at the sides and, with turntables, CDJs on stands above.",
      "Console: alignment guides appear while dragging to keep items tidy on the fomet.",
    ],
  },
  {
    version: "1.31.1",
    date: "2026-07-03",
    changes: [
      "Admin users table redone: sorting on every column, instant search, filters by tier/type/AI, pagination and row count.",
    ],
  },
  {
    version: "1.31.0",
    date: "2026-07-03",
    changes: [
      "Festival AI assistant: ask in plain language about times, stages, technical riders, hospitality, soundchecks, flights, hotels, transport, changeovers, equipment, crew, conflicts and production status. Sparkle button on the festival pages (for enabled accounts).",
      "New \"AI Access\" admin section: turn the assistant on or off per user and set a monthly message limit, with the month's usage visible.",
      "Flight number (arrival/departure) on hotel assignments — visible in Logistics and in the assistant's answers.",
      "Rider extraction upgraded to Claude Sonnet 5.",
    ],
  },
  {
    version: "1.30.2",
    date: "2026-07-02",
    changes: [
      "Console: when artists ask for different generations of the same CDJ (e.g. 3000 and 3000X), the shared console is now drawn entirely with the newest generation requested — whoever asks for the old model plays on the new one, not the other way round.",
      "Console: a generic request (e.g. \"Technics SL-1210\" with no generation) is covered by the specific generation requested by the other artists on the stage.",
      "Console: truly different generations (e.g. Technics M5G and MK7) stay distinct as before — the larger request wins.",
    ],
  },
  {
    version: "1.30.1",
    date: "2026-07-02",
    changes: [
      "Console: the canvas now draws the real models requested in the riders (e.g. CDJ-3000X, Technics SL-1210 M5G or MK7) instead of the generic family label — footprints follow the real model too.",
      "Console: layouts saved before the fix keep working without duplicates; to see the new names on a stage already saved use \"Restore setup\".",
      "Console: no more freezes on large festivals — the stage strip no longer recomputes every layout on each interaction (zoom, hover and drag are now smooth).",
      "The festival reopens on the last view you were looking at when you come back, even inside Production (Console, Crew, ...); explicit ?tab= links always win.",
    ],
  },
  {
    version: "1.30.0",
    date: "2026-07-02",
    changes: [
      "Changeovers: the view is redesigned from scratch as a single operational surface — summary, action queue sorted by priority and a protocol panel for the selected changeover.",
      "Changeovers: the panel shows the console before/after each artist change, with OUT/IN/KEEP on every element, a change list by model and the changeover window.",
      "Changeovers: an honest timing verdict for every changeover — missing minutes in red, buffer with prep in amber, buffer within the gap in green — plus the recommended minimum crew.",
      "Changeovers: actions are aggregated by family (e.g. \"remove 3 backline\") without truncation, and on tablet and mobile clicking a row takes you straight to the protocol.",
    ],
  },
  {
    version: "1.29.3",
    date: "2026-07-01",
    changes: [
      "Schedule: Grid becomes the main view and the old Timeline no longer appears as a separate tab.",
      "Schedule: inside Grid you can switch between horizontal and vertical layout with two compact icons, keeping the same operational toolbar.",
      "Schedule: Inspector and Conflicts are shared between horizontal and vertical layout, so clicks on DJs, console requests, notes and conflicts stay in the same flow.",
      "Console DJ requests: the panel now shows the effective manual assignment; if it differs from the original request, the rider request stays visible as a reference.",
    ],
  },
  {
    version: "1.29.2",
    date: "2026-06-29",
    changes: [
      "Console: added a Peak / Day selector to see the stage's maximum setup or the setup tailored to a single day.",
      "Console: in day view saving stays blocked with a clear message, to avoid overwriting the stage's peak layout.",
    ],
  },
  {
    version: "1.29.1",
    date: "2026-06-29",
    changes: [
      "Original rider PDFs: TRACE now shows the download only when the original file is really available and clearly marks historical riders with no recoverable PDF.",
      "Fixed: compatibility with older `gs://` storage references, so PDFs saved on the bucket stay downloadable.",
    ],
  },
  {
    version: "1.29.0",
    date: "2026-06-29",
    changes: [
      "Production Console: a new interactive console with draggable fomets, CDJs, mixers and turntables, real dimensions, reset to the real setup, standard setup and an exportable PDF for the stages.",
      "Console + Equipment: elements added by hand and saved in the console now appear in Equipment as production extras, separate from rider requests.",
      "Console safety: if you delete an element requested by the rider, Save stays blocked and TRACE clearly shows which item is missing instead of saving an ambiguous layout.",
      "Rider intelligence: extraction now better preserves physical information about fomets, table dimensions and console placement when they are written in the rider.",
      "Rider documents: you can download the original uploaded PDF directly from the rider review/list.",
    ],
  },
  {
    version: "1.28.14",
    date: "2026-06-26",
    changes: [
      "Invite onboarding: people invited into an organization no longer see a false error after registering and verifying their email; the link now recognizes the automatic acceptance and takes them to the dashboard correctly.",
      "Registration: the captcha check handles expired tokens, timeouts and temporary Turnstile rate limits better, with clearer messages and automatic reset of the widget.",
      "Artist and crew invites: the links are more robust even when the account was already linked automatically during login or email verification.",
    ],
  },
  {
    version: "1.28.13",
    date: "2026-06-25",
    changes: [
      "Dashboard: a new operational control room with readiness score, per-stage chart and a clearer action list.",
      "Crew: it now shows people assigned vs people required, with the gap at a glance and direct access to the crew plan.",
      "Onboarding: added Replay onboarding directly from the dashboard to revisit the guided flow when you need it.",
    ],
  },
  {
    version: "1.28.12",
    date: "2026-06-25",
    changes: [
      "Dashboard: the New view is now more operational and easier to read, with Production status, main modules and less noise in the hierarchy.",
      "Dashboard: Explore modules returns to the home with contextual numbers for Schedule, Equipment, Crew and Logistics.",
    ],
  },
  {
    version: "1.28.11",
    date: "2026-06-25",
    changes: [
      "Fixed: the Safe edit toggle no longer breaks the festival page after saving.",
    ],
  },
  {
    version: "1.28.10",
    date: "2026-06-25",
    changes: [
      "Grid: new organization-level Safe edit. It is off by default; owners and superadmins can turn it on when they want every grid change to be confirmed.",
      "Schedule grid: with Safe edit on, moves, time changes and stage changes ask for confirmation before saving, while still keeping undo after the change.",
      "Grid permissions: owners and managers can edit the schedule; members see the grid read-only with no drag, resize or accidental deletions.",
    ],
  },
  {
    version: "1.28.9",
    date: "2026-06-22",
    changes: [
      "Production Pack: the By Stage Day sheet is now sorted by day and stage, with stable colored groups to read the operational setup faster.",
      "Production Pack: the Service Order distinguishes stage sum, daily peak, reusable gear, spares and the recommended quantity to order.",
      "Export: the Production Pack XLSX can be downloaded in English or Italian from the new Pack language option in the Export menu.",
    ],
  },
  {
    version: "1.28.8",
    date: "2026-06-19",
    changes: [
      "Production Equipment: Technics M5G/MK5G turntables without an explicit 1200/1210 are treated as SL-1210 M5G; promoter confirmations stay soft on Technics families but flag strong constraints such as MK2 only, no MK7 or no alternatives.",
    ],
  },
  {
    version: "1.28.7",
    date: "2026-06-17",
    changes: [
      "Export: new Production Pack XLSX, a production-readable workbook with overview, service order, setup per stage/day, promoter confirmations and legend.",
    ],
  },
  {
    version: "1.28.6",
    date: "2026-06-17",
    changes: [
      "Export menu: every entry now explains what the file is for before download, distinguishing timetable, equipment order, operational pull sheet and PDF report.",
    ],
  },
  {
    version: "1.28.5",
    date: "2026-06-16",
    changes: [
      "Production Equipment: the Promoter confirmations drawer now exports a CSV with artist, stage, time, rider request, assigned model and link to the rider.",
      "Equipment export: spares are now calculated consistently in the stage/day rows too; the Pull Sheet CSV shows Qty, Spare and Total.",
    ],
  },
  {
    version: "1.28.4",
    date: "2026-06-16",
    changes: [
      "Production Equipment: Promoter confirmations now also checks the rider's notes and original text, so it no longer flags CDJ-3000, V10 or other models when they were written in the PDF but not extracted as clickable alternatives.",
    ],
  },
  {
    version: "1.28.3",
    date: "2026-06-16",
    changes: [
      "Production Equipment: Promoter confirmations is now a drawer opened from a compact button, so it no longer steals room from the matrix.",
      "Production Equipment: the confirmations list now excludes cases where the manually chosen model was already an alternative accepted by the rider.",
      "Production Equipment: the desktop matrix no longer needs horizontal scrolling; stage names remain available via tooltip.",
    ],
  },
  {
    version: "1.28.2",
    date: "2026-06-16",
    changes: [
      "Conflicts: the Missing riders summary is now aligned under the main counts, easier to read in the side column.",
      "Grid: new artist search to know right away where and when a DJ plays, with clickable results and a blinking highlight on the set.",
      "Rider upload: duplicate protection based on the PDF content. If you upload the same rider under a different name it is blocked or skipped before extraction.",
      "Production Equipment: stage names are more readable and have a tooltip with the full name.",
      "Production Equipment: new Promoter confirmations list with CDJs, mixers and turntables assigned by hand compared to the original rider.",
    ],
  },
  {
    version: "1.28.1",
    date: "2026-06-13",
    changes: [
      "Production Equipment: opening a Band equipment or Live electronic row now always shows who requested it, with artist, stage, day, quantity and a link to the rider when available.",
      "Fixed: generic equipment rows such as DI Box, wedge, MIDI cable, laptop stand and other gear no longer show 'No rider source' when they come from an approved rider.",
    ],
  },
  {
    version: "1.28.0",
    date: "2026-06-13",
    changes: [
      "Mobile/PWA: a new dedicated app shell on iPhone and small screens, with a compact top bar, bottom navigation for Festivals, Riders, Production and Settings, and a mobile menu separate from the desktop sidebar.",
      "Rider Review on mobile: single-column layout, Source / Technical / Hospitality tabs, Save and Approve actions always reachable at the bottom and an equipment picker as a touch-friendly bottom sheet.",
      "Production Equipment on mobile: the desktop matrix gives way to a card list by category and stage, with expandable request details and links to the riders, without unmanageable wide tables.",
      "Equipment: when you work on a specific day, open a rider and go back, TRACE keeps the day filter instead of returning to MAX.",
      "PWA polish: a more compact workspace banner, full-screen search on mobile, iOS safe area and more consistent touch targets without changing the desktop UX.",
    ],
  },
  {
    version: "1.27.2",
    date: "2026-06-12",
    changes: [
      "Rider review: you can manually assign the operational model to use for CDJs, mixer and turntables by picking it from the catalog. The choice is saved as Assigned model and feeds Equipment and Changeovers.",
      "The rider's original request stays visible: if you assign a different model, the card shows both Assigned and Rider, with a control to go back to the PDF's request.",
      "The equipment catalog is now available via a read-only API to support consistent, searchable human selections.",
    ],
  },
  {
    version: "1.27.1",
    date: "2026-06-12",
    changes: [
      "Fixed: in rider review, when you promote a mixer or CDJ alternative the card immediately shows the newly chosen model and saves it as the effective primary model.",
      "Fixed: after saving a rider the Equipment views are refreshed too, so the counts no longer stay stuck on the original model.",
    ],
  },
  {
    version: "1.27.0",
    date: "2026-06-12",
    changes: [
      "Festival PDF export rebuilt as an operational document: a summary up front, Action Focus with real priorities, more compact and readable tables, uniform badges and fewer useless pages.",
      "Run of show: extra events are now recognizable production pins. Events created by the organization can be edited or deleted straight from the row, while artist events stay visible as external sources.",
      "Equipment: every model can be opened to see who asks for it, on which stage/day, with a link to the rider and the accepted alternatives. Handy to see right away whether an artist asking for a Xone:96 also accepts a V10 or a house mixer.",
      "Equipment: added the operational distinction Required / In house / To service. In-house stock is entered per stage and TRACE immediately works out what is left to send to the supplier.",
      "Equipment Board cleaned up: Req/House/Service legend, less repeated text in the cells, more compact indicators and visual focus on the numbers that matter.",
    ],
  },
  {
    version: "1.26.1",
    date: "2026-06-12",
    changes: [
      "Requested variants are now ALWAYS visible in the Equipment grid: under each grouped row you see sub-rows with the exact model each artist asked for and the quantities per stage (\"↳ SL-1210 MK7 — 2\"). No more hidden tooltips: whoever is working sees at once who wants what, in the per-day view and in the Classic view too.",
      "CDJ-3000 and CDJ-3000X are now a single row: it is the same player for whoever builds the stage (the X is the 2025 revision). The sub-row still tells you who asked for the X.",
      "Changeover corrected by category: for CDJs generations are interchangeable (CDJ-2000 → NXS2 or 3000 → 3000X is not a change: everyone plays on the house player), for turntables they are not — SL-1210 M5G → MK7 is flagged as a real change, because they are different decks. Different spellings of the same machine (M5G ≡ MK5G) are never a change.",
    ],
  },
  {
    version: "1.26.0",
    date: "2026-06-12",
    changes: [
      "Equipment grouped by what you rent: Technics now sit in just two rows — SL-1200 (silver) and SL-1210 (black) — because in practice you stock one type only, and every generation (MK2…MK7, M5G) flows into it. The same goes for the CDJ-2000/2000NXS, which flow under the CDJ-2000NXS2. Hover over the row to see the generations and spellings each artist actually requested.",
      "Correct counts: before, five different spellings of the same turntable added up their numbers and inflated the totals (12 decks where 3 were needed). The row now shows the real peak requirement.",
      "Catalog updated to 2026: added the new Pioneer CDJ-3000X (the AlphaTheta flagship), the CDJ-2000 line, the PLX-CRSS12, the classic Technics and more. Generic rider phrases (\"Technics 1200 or 1210\") land on the club standard, the SL-1210.",
      "Import limit raised to 80 files per upload (admin).",
    ],
  },
  {
    version: "1.25.0",
    date: "2026-06-11",
    changes: [
      "Set detail in Timeline: clicking a card now shows the technical setup at a glance RIGHT AWAY — how many players and turntables (with model), which mixer, the booth monitors — plus the hard constraints highlighted (exclusive use, on-site spare, full-range). Before, DJs only got the descriptive notes.",
      "The same panel reorganized for bands (members, channels, backline, monitor mix — with channels read correctly) and live electronic (hardware, cables, artist brings), with changeover at the top and notes at the bottom.",
    ],
  },
  {
    version: "1.23.1",
    date: "2026-06-11",
    changes: [
      "HTML timetable export: rebuilt with the new visual language — light theme, one proportional lane per stage with cards as wide as the real duration, identity colors per stage, an hour ruler and a green dot for linked riders. Print-ready too.",
    ],
  },
  {
    version: "1.23.0",
    date: "2026-06-11",
    changes: [
      "Stage Grid (New): the 2-hour blocks are gone — it is now a proportional horizontal timeline: every set is placed and sized by its real duration, with the hour ruler as reference, and the scale adapts to the screen width (short days fill the space, very long ones scroll horizontally). No more columns that look empty while a set is playing.",
      "Drag rebuilt: while you drag, a dashed ghost shows the exact landing spot with the destination time (15-minute steps), on release the card glides smoothly into place with no waiting, and saving happens behind the scenes with data always in sync.",
    ],
  },
  {
    version: "1.22.2",
    date: "2026-06-10",
    changes: [
      "Crew: the assignments table and the timeline load much faster — every request used to drag along the owner's whole festival portfolio (riders included) because of cascading database loads; now every endpoint loads only what it needs.",
      "Crew: after an assignment the view refreshes right away (the refetch is now almost instant) and when you change day the previous view stays visible while the new one loads, with no empty flashes.",
      "What's new: people entering TRACE for the first time no longer see the changelog popup — it will appear only from the next release onward.",
    ],
  },
  {
    version: "1.22.1",
    date: "2026-06-10",
    changes: [
      "Equipment: model recognition is more cautious — a typo in the letters is corrected, but a different model number (CDJ-3000 vs CDJ-2000) is never mixed up. Added to the catalog: the classic Technics SL-1200/1210 and MK2, TR-8S, APC40 and the most common effects pedals (95 models).",
    ],
  },
  {
    version: "1.22.0",
    date: "2026-06-10",
    changes: [
      "Equipment: model names are now unified — \"Pioneer DJ CDJ 3000\", \"Pioneer CDJ-3000\" and \"CDJ3000\" count as the same deck in the pull sheet and rental totals (this also applies to Technics, mixers and the rest of the catalog, expanded to 83 models).",
      "Equipment: live electronic hardware and effects are recognized from the catalog too, and off-list gear no longer all ends up in the \"other\" row.",
      "Stability: AI-extracted data is cleaned on save — missing quantities or durations get safe defaults, removing at the root a class of errors in the intelligence tabs.",
      "Admin: renormalizing existing riders now has a dry-run mode that previews what would change and which models the catalog does not recognize.",
    ],
  },
  {
    version: "1.21.0",
    date: "2026-06-10",
    changes: [
      "Global search: the top bar (⌘K) now really searches — festivals by name or location, riders by artist or file name, and the app's pages. Grouped results, arrow-key navigation, Enter to open.",
      "Rider deletion: the confirmation is no longer the browser popup but an inline window in the design, with the names of the selected artists and progress while it deletes.",
      "AI updated: rider extraction and email drafts now use Claude Sonnet 4.6 (a newer, more accurate model at the same cost) and OCR/classification moves to the stable Haiku 4.5 alias.",
    ],
  },
  {
    version: "1.20.0",
    date: "2026-06-10",
    changes: [
      "Riders: the original PDF file name is saved on upload and shown in the rider detail and list (and it is searchable) — handy when tech rider and hospitality arrive as two separate files from the same artist.",
      "Riders uploaded before today have no recorded name: it will appear for all upcoming uploads and URL imports.",
    ],
  },
  {
    version: "1.19.1",
    date: "2026-06-10",
    changes: [
      "Festivals: the location filter is back next to the search (it had disappeared with the list redesign).",
      "Fixed: on festival cards the color change and trash controls no longer overlap the status — on hover they take the pill's place.",
    ],
  },
  {
    version: "1.19.0",
    date: "2026-06-10",
    changes: [
      "New design: TRACE has a completely new look — light and dark theme, an identity color for every stage used across all views, and the festival detail reorganized into 3 sections (Schedule, Production, Artist Portal) with conflicts always visible at the top.",
      "Schedule Timeline: a new column-per-stage view on a time axis — set durations are proportional, stage changes appear as dashed bands with minutes and conflicts are highlighted right on the schedule. Click a set for the detail with its rider.",
      "Stage Grid: a new grid in 2-hour bands — drag a set along its stage row to move it (the duration is preserved).",
      "Renewed dashboard: the upcoming festival with countdown and review progress per stage, a \"needs your attention\" list (conflicts and riders to review) and compact rider upload.",
      "Crew: a new horizontal timeline of shifts, including those that run past midnight.",
      "Equipment: a new rental matrix of models × stages with totals and a day filter (MAX or single day).",
      "The redesigned views bring the new operational experience directly into every module.",
      "Fixed: OCR of scanned PDFs and crew photo uploads no longer slow down other requests.",
    ],
  },
  {
    version: "1.18.0",
    date: "2026-06-08",
    changes: [
      "Fixed: a database connection lock that caused 500 errors on login, upload and deletions when several operations ran at once.",
      "Stability: slow operations (Stripe, PDF generation, file upload, maps, AI) no longer block other requests — the app holds up much better with several people at the same time.",
      "Fixed: Run Optimization, the Conflicts tab, the Equipment tab and the report export no longer fail when a rider has missing soundcheck data or quantities.",
      "Conflicts: a new filter by conflict type and a summary with the number and percentage of missing riders.",
      "Upload: admins and super-admins can upload up to 40 PDFs at a time (the limit was 10).",
      "Bulk rider deletion: now a single operation, with a clear confirmation of how many were deleted.",
      "Fixed: going back from a DJ's detail no longer loses the selected day.",
    ],
  },
  {
    version: "1.17.2",
    date: "2026-06-05",
    changes: [
      "Stage Grid: when you add an act after other acts on the same stage, day and times start automatically from the last existing set, with a default duration of 90 minutes.",
    ],
  },
  {
    version: "1.17.1",
    date: "2026-06-05",
    changes: [
      "Fixed: clicks on Publish, Unpublish, Auto-assign, Export, Share and Copy now handle API/clipboard errors with a toast instead of generating unhandled promises in the monitoring tools.",
      "Fixed: Add Slot no longer inserts the same artist several times when you press Add twice quickly.",
      "Stage Grid: more room on large screens, with a wider central container and less compressed stage columns.",
      "Fixed: the dev Docker build installs the tools needed to compile native Python dependencies such as timezonefinder.",
    ],
  },
  {
    version: "1.17.0",
    date: "2026-05-05",
    changes: [
      "Welcome redesigned: on first login you now see a single screen with an animated extraction demo (PDF → equipment in 8 seconds) and a row of icons for the advanced features (Hotels, Transport, Crew, Equipment, Optimization). It replaces the old 17-step driver.js tour — more focused and less friction.",
      "Dashboard: a new persistent \"Get started\" checklist with 4 core steps (upload your first rider → create a festival → approve and assign → publish the timetable) + 1 optional bonus (grow the team). Completion is derived from your real data — nothing to tick by hand. When you complete the 4 core steps a celebration banner appears once, then the checklist collapses into a reopenable pill.",
      "Dashboard: an \"Explore more\" card next to the checklist with direct access to the advanced features of your first festival (Hotels, Transport, Crew, Equipment).",
      "Artists: people who join only as artists see a dedicated welcome screen that takes them straight to \"My Schedule\".",
      "Settings: \"Replay onboarding tour\" becomes \"Show welcome again\" — it reopens the modern welcome (and the celebration banner if you had already completed the checklist).",
    ],
  },
  {
    version: "1.16.0",
    date: "2026-05-02",
    changes: [
      "Fixed: freshly extracted riders not yet assigned to a slot now stay visible even when you have an active organization in the switcher. Before, they vanished from the list until you bound them — the v1.15.0 behavior is corrected. Once a rider is assigned to a slot it is scoped strictly to the festival's org (no leaks between orgs).",
      "Dashboard: it now shows the active organization's colored banner, like the other pages. Without it, it was unclear whether the rider/festival count belonged to your org or another one.",
      "PWA install banner on iPhone Safari: it appears at the bottom when you open TRACE from Safari (not from the home screen) with instructions to add it to the home. Dismissible, it comes back after 30 days if you have not installed it.",
      "Explicit refresh button in the mobile top bar: pull-to-refresh does not work on iOS standalone, so you now have a visible button that invalidates every query and reloads the data.",
    ],
  },
  {
    version: "1.15.0",
    date: "2026-05-02",
    changes: [
      "Security: who sees what has been reviewed. Every endpoint that reads riders and festivals now goes through a single centralized scoping module — no more ad-hoc visibility filters scattered through the code that could forget a case.",
      "Security: the 'root admin' identity (reserved, controlled via a server env var) is separated from the operational 'admin' flag in the DB. Promoting or demoting another admin now requires a two-step confirmation — an 8-character code sent by email, 15-minute expiry, single-use.",
      "Org switcher: a new 'All organizations' option in the sidebar switcher — see the festivals and riders of all your orgs at once. Selecting a specific org filters strictly (no more content from other orgs showing up through legacy privileges).",
      "Fixed: hotel auto-link in assignments — riders uploaded by an organization member are now found even when the festival is owned by another member. Same class of bug as 1.14.1, on a different code path.",
      "Fixed: the promote/demote admin dialog no longer leaves 'ghost' tokens in the DB if the email send fails — the confirmation email now goes out before the commit.",
    ],
  },
  {
    version: "1.14.2",
    date: "2026-05-01",
    changes: [
      "Fixed: organization members now see the festival's riders even when the rider is not linked directly to the festival but arrives through two other paths — assignment to a slot or binding to an artist on the org's roster. Real case: a user invited into Polifonic saw no riders even though many had been uploaded.",
    ],
  },
  {
    version: "1.14.1",
    date: "2026-05-01",
    changes: [
      "Fixed: members invited into an organization could not see the riders uploaded by the other members of the same org. Reads now go through the organization filter, no longer only through who uploaded.",
      "Writes (edit, delete, batch-approve, re-extract) stay bound to the uploader for now — the broader write-side sweep landed in 1.15.0.",
    ],
  },
  {
    version: "1.14.0",
    date: "2026-05-01",
    changes: [
      "Production Timeline (Scale plan): a new chronological view per festival day that brings together in one list the artists' sets, the events (soundcheck, lobby call, transfer, press, meal, etc.) and the changeovers inferred automatically from the gaps between consecutive slots on the same stage.",
      "Day picker, grouping by hour, colored badges for every event type — set cyan, soundcheck purple, lobby amber, transfer blue, press pink, meal green, changeover orange.",
      "Available as a dedicated tab on the festival page.",
    ],
  },
  {
    version: "1.13.3",
    date: "2026-04-30",
    changes: [
      "Crew: when you add a crew member with an email that already belongs to an active user account in your organization, the link is immediate — no more email-invite round trip for someone who is already in. They show up in the shifts right away.",
      "Admin endpoint to retroactively apply the same logic to crew members created before this fix.",
    ],
  },
  {
    version: "1.13.2",
    date: "2026-04-30",
    changes: [
      "Crew: setup (load-in) and teardown (load-out) days now appear as amber pills in the day selector if you have someone assigned on those days. Distinct from programming days, on demand.",
      "Buffer days extended from ±2 to ±4 days around the festival — useful for large productions with rigging, trussing and preliminary soundchecks.",
      "Amber banner + 'Setup (load-in)' / 'Teardown (load-out)' label on the schedule grid when you select a buffer day, replacing the tier-based counter that does not apply outside programming days.",
    ],
  },
  {
    version: "1.13.1",
    date: "2026-04-30",
    changes: [
      "Speed: every Artist Portal endpoint drops from 5-10 seconds to under a second (~15-20× faster). Same for the dashboard and notifications. It was a cascade of SQL queries loading hundreds of MB of JSON on every request.",
      "Mobile: all the main layouts redone for iPhone — Festival list, Festival detail (compact header + responsive Stage Grid + Stage View with inline times), Riders list, Rider review, Crew, Artist Portal on the org side, Organization page (Members + Artists). No more horizontal overflow.",
      "Fixed: artists saw an empty transport block in their Itinerary. It was a side effect of the performance fix: the DB session had cached the festival with its relations 'switched off', the logistics calculation reused the cache and produced zero hotels/zero slots. The calculation now forces the loading of the relations it needs.",
      "Logistics: the section is split into 5 sub-tabs (Overview, Hotels, Assignments, Transport, Contacts). 'Emergency Contacts' is now the 'Contacts' sub-tab in here, no longer a separate tab. Alerts (missing assignments, tight transfers, etc.) move inside Assignments with a counter badge on the sub-tab.",
      "Sidebar: reorganized into 3 sections — Artist, Production, Public. 'Discover' gets its own dedicated section.",
      "Dashboard: a hero card at the top for quick rider upload — drag and drop or paste a Drive/Dropbox link.",
      "Installable PWA: from iPhone Safari, Share → 'Add to Home Screen' opens TRACE standalone (no browser chrome). A branded offline page when there is no network.",
      "More readable tabs and sub-tabs: the active state has a cyan accent + a more visible background in both light and dark mode.",
    ],
  },
  {
    version: "1.13.0",
    date: "2026-04-29",
    changes: [
      "Festival time zone: every festival now has an IANA time zone (e.g. Europe/Rome, Asia/Tokyo). It is auto-suggested from the venue address on first save; a dropdown in the Venue panel lets you override it by hand.",
      "Artist Portal: Itinerary, Lineup and Overview times are now rendered in the festival's time zone. The 'times in UTC' disclaimer is replaced by the real zone name (e.g. 'times in Europe/Rome').",
      "Add/Edit event: the two Schedule Events dialogs (artist-side and org-side) now ask for the time in the festival's local time (label '(local — Europe/Rome)' instead of '(UTC)') and convert internally for storage.",
      "Fixed: hotel check-in 15:00 and check-out 11:00 were forced to UTC regardless of the venue; they now follow the festival's time zone. Same for the transport events generated by logistics.",
      "Data migration: times saved in v1.12 (interpreted as UTC but actually typed as festival local time) were re-adjusted in the production DB through an idempotent backfill script with --apply / --reverse flags.",
    ],
  },
  {
    version: "1.12.0",
    date: "2026-04-24",
    changes: [
      "Artist Portal v1: invited artists now have a per-festival portal with 5 tabs — Overview, Lineup, Itinerary, Documents and Emergency Contacts. Reach it from 'My Schedule' by clicking 'Open portal →' on a festival card.",
      "Documents: the organization can upload flight tickets, contracts, itineraries and other PDF/JPEG/PNG files (max 25 MB) for each artist; the artist downloads them with a signed link that lasts 15 minutes.",
      "Itinerary: a chronological view that automatically merges the show + hotel check-in + transport + extra events (soundcheck, press, rehearsals). Overlapping events show a yellow warning.",
      "Schedule Events: the artist can add their own events (press, rehearsals, dinner) from the portal; the organization sees them in the admin tab with an 'Added by artist' badge.",
      "Emergency Contacts: the organization manages production contacts from the new 'Emergency' tab on the festival detail; the artist sees them in the portal with tap-to-call and tap-to-email.",
      "Notifications: 3 new types (document uploaded, event changed, event added by the artist) with a single 'Artist portal activity' toggle in the notification settings, on by default.",
    ],
  },
  {
    version: "1.11.5",
    date: "2026-04-20",
    changes: [
      "Fixed: a security gap — artist accounts invited into an organization can no longer upload riders, re-extract or run Google Maps searches that would be billed to the organization that invited them. Reading their own schedule, their own hotel and the places linked to their own assignments remains available.",
      "Invited artists can now update their own photo, phone and profile notes (before, the button seemed to work but returned an error).",
      "New organization flag (reserved for future use): artist_self_service_enabled — once turned on it will let invited artists perform productive actions within the organization's quota.",
    ],
  },
  {
    version: "1.11.4",
    date: "2026-04-20",
    changes: [
      "Fixed: a security gap on the artist path — an artist invited into an organization can no longer access exports, conflicts, equipment totals, crew or the transport Gantt of the whole festival. They see only their own data through /me/artist-dashboard, as originally intended.",
      "The hotel and airport lists for an invited artist now show only the hotel and airport of their personal assignment.",
    ],
  },
  {
    version: "1.11.3",
    date: "2026-04-20",
    changes: [
      "Fixed: the 1.11.2 security fix continues — the owner/manager restriction now also applies to crew assignments, hotels, transport, preferred airports and reassignments. A member can no longer delete or edit this data: read only.",
      "Validation added on artist profiles: it is no longer possible to link a slot to an artist who does not belong to the festival's organization.",
    ],
  },
  {
    version: "1.11.2",
    date: "2026-04-20",
    changes: [
      "Fixed: a security gap — only the owners and managers of an organization can edit or delete festivals, stages and slots. Members have read access and can create their own content.",
      "A festival's organization can no longer be changed after creation — it was a bug that allowed silently moving a festival to another org.",
      "The sidebar now shows the effective plan (the one you can actually use) instead of the plan you pay for personally. In Settings > Subscription you see your personal plan instead — that is the invoice page.",
      "In the admin area the user list now shows both the plan the user pays for and their effective access (which can be higher if they are invited into an Enterprise org). No more doubts about who has access to what.",
    ],
  },
  {
    version: "1.11.1",
    date: "2026-04-20",
    changes: [
      "Invite and verification emails completely redesigned: branded layout with header, animated cyan accent, a prominent call to action and a consistent footer. No more plain text.",
      "Password reset: you now really receive an email when you click 'Forgot password' (before, the link was only logged).",
      "The Festivals and Riders pages show the active organization's name in the title (e.g. 'Festivals · Skeldon'), as Crew already does.",
      "In Batch Assign Crew there is now a help line under the two radio buttons (Recurrence / Pick dates) explaining when to use them.",
      "Stability: if we deploy a new version while you have the app open, clicking a page not yet loaded now triggers a silent reload instead of showing 'Something went wrong'.",
    ],
  },
  {
    version: "1.11.0",
    date: "2026-04-20",
    changes: [
      "Organization banner at the top of the pages: an animated colored header with the active organization's name, so you know at once where you are working. Every organization has a unique, stable color.",
      "The title of organization pages now always shows the name: for example 'Crew · Skeldon'. No more doubts about the context.",
      "Clearer empty states: when a list is empty, the message says 'No X in Skeldon yet', with a link to switch organization — so you do not confuse 'no data' with 'maybe I picked the wrong org'.",
      "New preference in Settings to hide the colored banner if you prefer a more minimal interface.",
      "The banner animation respects your system preferences: if 'Reduce motion' is on in your accessibility settings, the banner stays still.",
    ],
  },
  {
    version: "1.10.3",
    date: "2026-04-20",
    changes: [
      "Internal stability improvements to prevent future crashes on pages with lots of linked data.",
    ],
  },
  {
    version: "1.10.2",
    date: "2026-04-20",
    changes: [
      "Fixed: the organization management page (for admins) now loads correctly even with several linked festivals.",
      "Roles in the Crew Members list now fit on a single line, easier to read.",
      "Download buttons (Daily Sheet PDF, CSV, Full festival PDF, personal schedule) show a loading indicator while we generate the file — no more wondering whether the click worked.",
    ],
  },
  {
    version: "1.10.1",
    date: "2026-04-20",
    changes: [
      "Admins can now populate the 41 predefined crew roles for existing organizations with one click (previously available only for new organizations).",
    ],
  },
  {
    version: "1.10.0",
    date: "2026-04-19",
    changes: [
      "Crew Contact Module (Scale plan): manage your whole production team — technicians, stage managers, assistants, catering, runners — in a single address book linked to the organization.",
      "41 ready-to-use roles (Stage Manager, FOH Engineer, Monitor Engineer, Production Director, Rigger, Runner, ...) — all customizable and extendable with your own.",
      "Invite your technicians by email: they accept with one click, log in with their own account and see only their own shifts.",
      "The Crew tab on every festival is split into two sections: Requirements (decide how many people each stage needs on each day) and Assignments (assign who does what, where and when).",
      "The production director has the last word: you can override the automatically calculated crew count for any stage/day combination.",
      "Bulk assignment: place a technician on several dates in one go — perfect for recurring events (e.g. every Monday from April to October).",
      "Build and strike days: you can schedule crew up to 2 days before and after the festival, for load-in, rehearsals and load-out.",
      "Conflict check: the same person assigned to two stages on the same day is flagged immediately with a warning.",
      "Export: Daily Sheet as PDF and CSV for each day + a full PDF of the whole festival with one page per day, ready to share with the team.",
      "'My Shifts' page: every technician sees all their shifts across every festival and downloads their personal schedule as a PDF.",
      "Night shifts handled correctly: a 16:00–02:00 set is read as 'until 2 a.m. the next day'.",
      "A festival's active tab stays in the URL: refreshing the page no longer takes you away from the tab you were looking at.",
    ],
  },
  {
    version: "1.9.1",
    date: "2026-04-16",
    changes: [
      "Fixed: the backline of band/live-electronic riders shows the equipment name (e.g. 'Moog Subsequent 37') instead of 'Unknown'.",
      "More precise equipment normalization: ambiguous names such as 'Pioneer CDJ' (no model number) no longer wrongly match the first similar model.",
      "Data cleanup: new 'Renormalize Riders' button in Settings > Admin Tools (superadmin only) — re-applies normalization to riders extracted before this release.",
      "Smart changeover: a quantity change on the same model (e.g. 2 CDJs → 3 CDJs) is now classified as a 'Minor Swap' with an estimated 1 extra minute per unit, instead of 'Zero Change'.",
      "Richer changeover diff: the green/red pill shows the quantity delta (e.g. 'CDJ: Pioneer DJ CDJ-3000 (2→3)').",
    ],
  },
  {
    version: "1.9.0",
    date: "2026-03-20",
    changes: [
      "PDF Report: generate a complete per-festival report with equipment, schedule, changeovers and conflicts per stage.",
      "Equipment normalization: equipment names are normalized automatically during extraction (e.g. 'cdj 3000' → 'Pioneer DJ CDJ-3000').",
      "Export dropdown: all exports (Timetable, CSV, Pull Sheet, PDF) are now grouped in a dropdown menu.",
      "Faster page loads: heavy pages load on demand (~40% lighter).",
      "Colored tier badges: Explorer (cyan), Builder (purple), Scale (amber), Enterprise (green).",
      "Fixed: slot drag-and-drop no longer changes the order of the other days.",
      "Fixed: the festival quota now correctly counts the organization's festivals too.",
      "Fixed: re-extract requires at least the Explorer tier.",
      "Fixed: the usage page shows the effective tier (not just the personal one).",
      "Security: security headers, JWT protection in production, improved rate limiter.",
    ],
  },
  {
    version: "1.8.0",
    date: "2026-03-01",
    changes: [
      "Editable hospitality: edit hospitality data inline on the review page (click-to-edit for single values, add/remove pills for lists).",
      "Re-extract with feedback: a progress banner with spinner during re-extraction, auto-refresh on completion.",
      "Improved hospitality section: sections with data open, empty ones collapsed, pills for lists, allergies in amber.",
      "Ground transport pills: vehicle type, dedicated driver and special requirements visible in the hospitality summary.",
      "Gantt alerts: logistics alerts are now visible in both the Timeline and the Gantt view.",
      "Collapsible assignments: expandable rows with inline edit and delete.",
      "Edit assignment: change hotel, dates, flights and notes directly from the assignment row.",
    ],
  },
  {
    version: "1.7.0",
    date: "2026-02-28",
    changes: [
      "Artist profiles and roster: manage your artist roster from the organization, with roles and email invites.",
      "Artist invite flow: invite link with registration, email verification and automatic acceptance.",
      "Artist sidebar: a dedicated section with My Schedule and Discover for users with an artist profile.",
      "Festival-org assignment: festivals are automatically assigned to the active organization on creation.",
      "Organization selector: a dropdown in the sidebar to switch the active organization.",
      "Invite security: email verification on every invite acceptance flow.",
      "Fixed: artist roster blocked in the personal org, with an informative message.",
      "Fixed: complete returnTo chain login → register → verify-email → accept-invite.",
    ],
  },
  {
    version: "1.6.0",
    date: "2026-02-26",
    changes: [
      "Fixed: changeover ordering and gaps for late-night sets that cross midnight.",
      "Transport Gantt export: export the transport chart as printable HTML.",
      "Logistics on the landing page: a new section presenting the hotel and transport module.",
      "Tiers updated: hospitality and logistics now visible in the Pro and Enterprise plans.",
      "Fixed: contrast of airport IATA pills, equipment changeover and rider review in the light theme.",
    ],
  },
  {
    version: "1.5.0",
    date: "2026-02-25",
    changes: [
      "Hospitality & Logistics: a new Logistics tab on the festival page to manage venue, hotels and transport.",
      "Venue setup: search and set the festival venue with Google Places autocomplete.",
      "Hotel management: add hotels by hand or discover nearby ones with Google Places.",
      "Artist-hotel assignments: assign artists to hotels with notes and check-in/out tracking.",
      "Transport timeline: view arrivals, departures, shuttles and transfers per day.",
      "Nearby airports: automatic distance calculation from the venue using coordinates.",
      "Hospitality extraction: the rider PDF now also yields hospitality data (allergies, hotel preferences, transfers).",
      "Logistics alerts: automatic warnings for late arrivals, artists without a hotel and transport conflicts.",
    ],
  },
  {
    version: "1.4.0",
    date: "2026-02-18",
    changes: [
      "Error tracking: Sentry integrated on backend and frontend to monitor errors in production.",
      "Source maps: readable stack traces on Sentry for quick debugging.",
      "Privacy: automatic scrubbing of emails and usernames from Sentry events.",
    ],
  },
  {
    version: "1.3.0",
    date: "2026-02-17",
    changes: [
      "Rider import from URL: paste a Google Drive or Dropbox link and the PDF is downloaded and processed automatically.",
      "Support for Google Drive and Dropbox share links (automatic conversion to direct download).",
      "SSRF protection: URL validation, HTTPS only, private IPs blocked.",
    ],
  },
  {
    version: "1.2.0",
    date: "2026-02-17",
    changes: [
      "Recurring festivals: create weekly events (e.g. every Saturday from June to September).",
      "Day picker and DayFilter show only the valid days for recurring festivals.",
      "'Every [day]' badge on festival cards and header.",
      "Slot assignment limited to the recurring days only.",
    ],
  },
  {
    version: "1.1.0",
    date: "2026-02-17",
    changes: [
      "The festival wizard now includes stage creation (step 2).",
      "Post-upload guide with next steps after extraction.",
      "Interactive multi-page tour for the complete workflow.",
      "Approve & Assign: assign a rider to a festival/stage right after approval.",
      "Equipment picker: choose among mixer/CDJ alternatives during review.",
      "Time auto-suggestion based on existing slots.",
      "Fixed: the rider status filter now works correctly.",
      "Fixed: the timetable opens in the browser instead of downloading a file.",
    ],
  },
  {
    version: "1.0.0",
    date: "2026-02-01",
    changes: [
      "Launch of the first version of TRACE.",
      "Upload and automatic extraction of rider PDFs (DJ, band, live electronic).",
      "Rider review and approval with HITL.",
      "Festival, stage and lineup management.",
      "Intelligence: equipment planner, changeover planner, conflict detection.",
      "Public timetable with a shareable link.",
      "In-app notifications for festival followers.",
      "Stripe subscriptions (Free, Starter, Pro, Enterprise).",
    ],
  },
];
